<template>
  <div class="wrapper">

    <div class="header_wrapper">
      <v-toolbar color="primary" class="position-relative">
        <v-btn
          @click="routerBack"
          icon
          variant="text"
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>

        <v-toolbar-title 
          class="flex-grow-1 text-center ml-0"
          style="pointer-events: none;"
        >
          家族設定
        </v-toolbar-title>

        <v-btn icon disabled style="visibility: hidden">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
      </v-toolbar>
    </div>

    <div class="pa-1 today-wrapper">
      <h5>保護者</h5>
    </div>
    <div>
      <v-list lines="two" density="compact">
        <!-- 1. key は template タグに移動 (v-list-item-group は削除推奨) -->
        <template v-for="(p, index) in parents" :key="p.parentId">
          <v-list-item
            @click="toParent('/parent/edit', 'ParentEdit', p.isLoginUser)"
          >
            <!-- 2. 左側の画像: prepend スロットを使用 -->
            <template v-slot:prepend>
              <v-avatar class="ml-2">
                <v-img :src="p.imageUrl ?? undefined" />
              </v-avatar>
            </template>

            <!-- 3. 中央のコンテンツ: v-list-item-title / subtitle を直下に配置 -->
            <v-list-item-title class="font-weight-bold subtitle-1">
              <div class="d-flex align-center">
                <span>{{ p.lastName + p.firstName }}</span>
                <div
                  v-if="p.isLoginUser"
                  class="login_user_badge pa-1 ml-1 body-2 bg-light-blue-lighten-2 text-white"
                >
                  あなた
                </div>
              </div>
            </v-list-item-title>

            <v-list-item-subtitle class="subtitle-2 text-grey-darken-5">
              <span>{{ formatParentType(p.parentType) }}</span>
            </v-list-item-subtitle>

            <!-- 4. 右側のアイコン: append スロットを使用 -->
            <template v-slot:append>
              <div v-if="p.isLoginUser" class="badge d-flex justify-center align-center mr-2">
                <v-icon>mdi-chevron-right</v-icon>
              </div>
            </template>
          </v-list-item>

          <v-divider />
        </template>
      </v-list>
    </div>
    <div class="px-5 py-2">
      <v-btn
        @click="to('/parent/add', 'ParentAdd')"
        color="primary"
        variant="outlined"
        block
        small
      >
        <v-icon>mdi-plus</v-icon>
        他の保護者を招待する
      </v-btn>
    </div>

    <div class="pa-1 today-wrapper">
      <h5>お子さま</h5>
    </div>
    <div>
      <v-list lines="two" density="compact">
        <!-- 1. key は template タグに移動します -->
        <template v-for="(c, index) in children" :key="c.childId">
          
          <v-list-item
            @click="toChild('/child/edit', 'ChildEdit', c.childId, c.lastName + c.firstName)"
          >
            <!-- 2. 左側の画像: prepend スロット + v-avatar を使用 -->
            <template v-slot:prepend>
              <v-avatar class="ml-2">
                <v-img :src="c.imageUrl ?? undefined" />
              </v-avatar>
            </template>

            <!-- 3. 中央のテキスト: v-list-item-content は廃止、title を直下に配置 -->
            <v-list-item-title class="font-weight-bold subtitle-1">
              <span>{{ c.lastName + c.firstName }}</span>
            </v-list-item-title>

            <!-- 4. 右側の矢印: append スロットを使用 -->
            <template v-slot:append>
              <div class="badge d-flex justify-center align-center mr-2">
                <v-icon>mdi-chevron-right</v-icon>
              </div>
            </template>
          </v-list-item>

          <v-divider />
        </template>
      </v-list>
    </div>
    <div class="px-5 py-2">
      <v-btn
        @click="to('/child/add', 'ChildAdd')"
        color="primary"
        variant="outlined"
        block
        small
      >
        <v-icon>mdi-plus</v-icon>
        お子さまを追加する
      </v-btn>
    </div>

    <div class="pa-1 today-wrapper">
      <h5>家族情報</h5>
    </div>
    <div>
      <v-list lines="two" density="compact">
        <v-list-item
          @click="to('/family/edit', 'FamilyEdit')"
        >
          <template v-slot:prepend>
            <v-avatar class="ml-2">
              <v-img src="https://via.placeholder.com/240x180?text=Sample+Image+1"/>
            </v-avatar>
          </template>

          <v-list-item-title class="font-weight-bold subtitle-1">
            <span>{{ family?.familyName }}</span>
          </v-list-item-title>

          <template v-slot:append>
            <div class="badge d-flex justify-center align-center mr-2">
              <v-icon>mdi-chevron-right</v-icon>
            </div>
          </template>
        </v-list-item>
        
        <v-divider />
      </v-list>
    </div>

  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { defineComponent } from 'vue';
import dayjs from 'dayjs';
import LocalDataService from '@/service/LocalDataServices';
import { when } from '@/service/Switch';

import { meModule } from '@/store/entityModules/me';
import { familyModule } from '@/store/entityModules/family';

// model
enum ParentType {
  MOTHER = 'mother',
  FATHER = 'father',
  GRANDMA = 'grandma',
  GRANPA = 'granpa',
  UNCLE = 'uncle',
  AUNT = 'aunt',
  SISTER = 'sister',
  BROTHER = 'brother',
  OTHER = 'other',
}

interface DataType {}

export default defineComponent({
  name: 'Family',
  components: {},

  data: (): DataType => ({}),

  computed: {
    family() {
      return familyModule.family;
    },
    parents() {
      if (familyModule.family) {
        return familyModule.family.parents;
      }
      return [];
    },
    children() {
      if (familyModule.family) {
        return familyModule.family.children;
      }
      return [];
    },
    today(): string {
      return dayjs().locale('ja').format('M月D日');
    },
  },

  methods: {
    routerBack() {
      this.$router.replace('/setting/various');
    },
    formatParentType(parentType: ParentType): string {
      return when(parentType)
        .on((v) => v === ParentType.MOTHER, () => '母')
        .on((v) => v === ParentType.FATHER, () => '父')
        .on((v) => v === ParentType.GRANDMA, () => '祖母')
        .on((v) => v === ParentType.GRANPA, () => '祖父')
        .on((v) => v === ParentType.UNCLE, () => '叔父')
        .on((v) => v === ParentType.AUNT, () => '叔母')
        .on((v) => v === ParentType.SISTER, () => '姉妹')
        .on((v) => v === ParentType.BROTHER, () => '兄弟')
        .on((v) => v === ParentType.OTHER, () => 'その他')
        .otherwise(() => '');
    },
    to(path: string, name: string) {
      this.$router.push({ name });
    },
    toParent(path: string, name: string, isLoginUser: boolean) {
      if (isLoginUser) {
        this.$router.push({ name });
      }
    },
    toChild(path: string, name: string, childId: string, childName: string) {
      this.$router.push({ name, query: {
        childId: String(childId),
        childName,
      } });
    },
  },

  mounted() {
    if (this.family) {
      familyModule.fetchFamilyById({familyId: this.family.familyId});
    }
  },
});
</script>
<style lang="scss" scoped>
@use '../sass/ress.scss';

/* ===== レイアウト ===== */
.wrapper > :not(.header_wrapper) {
  margin-top: 8px;
}
.wrapper {
  padding-top: calc(var(--app-top-offset) + 56px); // ← ヘッダー分
  margin-bottom: calc(env(safe-area-inset-bottom) + 56px);
}

/* ===== ヘッダー ===== */
.header_wrapper {
  height: 56px;
  width: 100%;
  position: fixed;
  top: var(--app-top-offset); // ← これが重要
  left: 0;
  z-index: 100;

  .outline {
    border: 1px solid rgb(var(--v-theme-primary));
  }
}

/* ===== セクション見出し ===== */
.today-wrapper {
  color: #A3A4A4;
  background-color: #F5F6F6;
}

/* ===== アイコン系 ===== */
.icon {
  height: 35px;
  width: 35px;

  img {
    height: 100%;
    width: 100%;
    border-radius: 100%;
  }
}

/* ===== ログインユーザバッジ ===== */
.login_user_badge {
  border-radius: 10%;
  font-weight: 500;
  color: #ffffff;
  background-color: rgb(var(--v-theme-success));
}

/* ===== 右矢印の余白調整 ===== */
.badge {
  min-width: 24px;
}

/* ===== テキスト省略 ===== */
.feed-subtitle {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
